import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { fetchSegmentCustomers } from "../../../api/queries";
import { useLanguage } from "../../../contexts/LanguageContext";
import { formatNumber, formatRelative } from "../format";
import { sampleSegmentCustomers } from "../sampleData";
import {
  HEALTH_SEGMENTS,
  SEGMENT_COLORS,
  segmentCustomersQueryKey,
  segmentLabelKey,
} from "../segments";
import ExportButton from "./ExportButton";
import { EmptyNote, Panel, Skeleton } from "./ui";

// Customers grouped by days since their last scan. Picking a segment lists
// its customers and offers a push to exactly that group.
export default function HealthSegments({
  storeId,
  health,
  loading,
  preview = false,
  canPush,
  canExport,
  onCreatePush,
}) {
  const { t, language } = useLanguage();
  const [segment, setSegment] = useState("at_risk");
  const counts = health || {};
  // The tiles come from the cached insights payload; the CTA and the push
  // button use the live total of the list below (same query, page 1), so the
  // button is never disabled while the list already shows customers.
  const live = useQuery({
    queryKey: segmentCustomersQueryKey(storeId, segment, 1),
    queryFn: () => fetchSegmentCustomers(storeId, segment, { page: 1 }),
    enabled: !preview && !!storeId,
  });
  const liveTotal = preview ? undefined : live.data?.total_count;
  const selectedCount = liveTotal ?? counts[segment] ?? 0;

  return (
    <Panel
      title={t("dashboard.insights.health.title")}
      subtitle={t("dashboard.insights.health.subtitle")}
      className="flex-[2_1_560px]"
    >
      {loading ? (
        <>
          <Skeleton className="h-3.5 w-full rounded-full" />
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
            {HEALTH_SEGMENTS.map((s) => (
              <Skeleton key={s} className="h-20" />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="flex h-3.5 gap-[3px] overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
            {HEALTH_SEGMENTS.map((s) =>
              counts[s] ? (
                <div key={s} style={{ flex: `${counts[s]} 1 0`, background: SEGMENT_COLORS[s] }} />
              ) : null,
            )}
          </div>
          <div
            role="group"
            aria-label={t("dashboard.insights.health.segmentLabel")}
            className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5"
          >
            {HEALTH_SEGMENTS.map((s) => {
              const on = s === segment;
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setSegment(s)}
                  className={`flex min-w-0 flex-col items-start gap-1 rounded-[10px] px-3.5 py-3 text-left ${
                    on ? "border-2 border-indigo-600 bg-indigo-50/50" : "border border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <span className="flex items-center gap-2 text-[13px] font-medium text-gray-700">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-[3px] ring-1 ring-gray-200"
                      style={{ background: SEGMENT_COLORS[s] }}
                      aria-hidden="true"
                    />
                    {t(segmentLabelKey(s))}
                  </span>
                  <span className="text-[22px] font-semibold tracking-tight text-gray-900">
                    {formatNumber(counts[s] ?? 0, language)}
                  </span>
                  <span className="text-xs text-gray-500">{t(`dashboard.segments.${s}.range`)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}

      <SegmentCustomers storeId={storeId} segment={segment} preview={preview} />

      {!loading && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] bg-gray-50 px-4 py-3.5">
          <span className="text-sm text-gray-700">
            {t(`dashboard.segments.${segment}.cta`, {
              value: formatNumber(selectedCount, language),
            })}
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {canExport && (
              <ExportButton key={segment} storeId={storeId} type="segment" segment={segment} variant="secondary" />
            )}
            {canPush && (
              <button
                type="button"
                onClick={() => onCreatePush?.(segment)}
                disabled={selectedCount === 0}
                className="min-h-11 rounded-[10px] bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {segment === "active"
                  ? t("dashboard.insights.health.createPush")
                  : t("dashboard.insights.health.createWinBack")}
              </button>
            )}
          </div>
        </div>
      )}
    </Panel>
  );
}

function SegmentCustomers({ storeId, segment, preview }) {
  const { t, language } = useLanguage();
  const [pageBySegment, setPageBySegment] = useState({});
  const page = pageBySegment[segment] || 1;

  const query = useQuery({
    queryKey: segmentCustomersQueryKey(storeId, segment, page),
    queryFn: () => fetchSegmentCustomers(storeId, segment, { page }),
    enabled: !preview && !!storeId,
    placeholderData: keepPreviousData,
  });

  const data = preview ? sampleSegmentCustomers : query.data;
  const loading = !preview && query.isLoading;
  const customers = data?.customers || [];
  const total = data?.total_count ?? 0;
  const perPage = data?.per_page || 50;
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const now = new Date();

  const goTo = (next) =>
    setPageBySegment((prev) => ({ ...prev, [segment]: Math.min(Math.max(1, next), lastPage) }));

  // The segment shrank while a later page was open: go back to its last page
  // instead of showing an empty list with no way back.
  useEffect(() => {
    if (!preview && query.data && page > lastPage) {
      setPageBySegment((prev) => ({ ...prev, [segment]: lastPage }));
    }
  }, [preview, query.data, page, lastPage, segment]);

  if (loading) return <Skeleton className="h-48 w-full" />;
  if (!preview && query.isError) {
    return <EmptyNote>{t("dashboard.error.body")}</EmptyNote>;
  }
  if (customers.length === 0) {
    return <EmptyNote>{t("dashboard.insights.health.noCustomers")}</EmptyNote>;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative overflow-x-auto rounded-[10px] border border-gray-100">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th scope="col" className="px-3.5 py-2.5 text-xs font-semibold text-gray-600">
                {t("dashboard.insights.health.table.customer")}
              </th>
              <th scope="col" className="px-3.5 py-2.5 text-xs font-semibold text-gray-600">
                {t("dashboard.insights.health.table.lastVisit")}
              </th>
              <th scope="col" className="px-3.5 py-2.5 text-right text-xs font-semibold text-gray-600">
                {t("dashboard.insights.health.table.visits")}
              </th>
              <th scope="col" className="px-3.5 py-2.5 text-right text-xs font-semibold text-gray-600">
                {t("dashboard.insights.health.table.balance")}
              </th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-t border-gray-100">
                <td className="px-3.5 py-2.5">
                  <Link
                    to={`/customers/${encodeURIComponent(c.uuid || c.id)}`}
                    className="font-medium text-gray-900 hover:text-indigo-700"
                  >
                    {c.name || t("dashboard.activity.unknownCustomer")}
                  </Link>
                </td>
                <td className="px-3.5 py-2.5 text-gray-600">
                  {c.last_visit_at ? formatRelative(c.last_visit_at, language, now) : t("dashboard.insights.health.table.never")}
                </td>
                <td className="px-3.5 py-2.5 text-right text-gray-700">
                  {formatNumber(c.visits ?? 0, language)}
                </td>
                <td className="px-3.5 py-2.5 text-right text-gray-700">
                  {formatNumber(Number(c.balance) || 0, language)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lastPage > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[13px] text-gray-600">
          <span>
            {t("dashboard.insights.health.pageOf", { page, pages: lastPage })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => goTo(page - 1)}
              disabled={page <= 1}
              className="min-h-9 rounded-lg border border-gray-200 px-3 font-medium text-gray-700 disabled:opacity-40"
            >
              {t("dashboard.insights.health.previous")}
            </button>
            <button
              type="button"
              onClick={() => goTo(page + 1)}
              disabled={page >= lastPage}
              className="min-h-9 rounded-lg border border-gray-200 px-3 font-medium text-gray-700 disabled:opacity-40"
            >
              {t("dashboard.insights.health.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
